import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../App";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Checkbox } from "../components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { 
  Users, 
  AlertTriangle,
  RefreshCw,
  UserMinus,
  UserPlus,
  Loader2,
  Building2,
  Phone,
  CheckCircle2,
  XCircle,
  Clock,
  ArrowRight,
  Filter,
  Eye,
  ChevronRight,
  Calendar
} from "lucide-react";
import { Input } from "../components/ui/input";

const LeadManagement = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [agents, setAgents] = useState([]);
  const [selectedAgentId, setSelectedAgentId] = useState("_select_");
  const [selectedAgentData, setSelectedAgentData] = useState(null);
  
  // Lead filter: "all", "untouched", "no_lfb", "has_lfb"
  const [leadFilter, setLeadFilter] = useState("all");
  const [dataSourceFilter, setDataSourceFilter] = useState("_all_");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [agentLeads, setAgentLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [isLoadingLeads, setIsLoadingLeads] = useState(false);
  
  // Selection for deallocation
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [selectAll, setSelectAll] = useState(false);
  
  // Reallocation
  const [showReallocateDialog, setShowReallocateDialog] = useState(false);
  const [targetAgentId, setTargetAgentId] = useState("_unassign_");
  const [isReallocating, setIsReallocating] = useState(false);

  useEffect(() => {
    fetchAgents();
  }, []);

  useEffect(() => {
    if (selectedAgentId && selectedAgentId !== "_select_") {
      fetchAgentLeads(selectedAgentId);
    } else {
      setAgentLeads([]);
      setFilteredLeads([]);
      setSelectedAgentData(null);
    }
  }, [selectedAgentId]);

  useEffect(() => {
    applyFilter();
  }, [leadFilter, dataSourceFilter, dateFrom, dateTo, agentLeads]);

  useEffect(() => {
    // Reset selection when filter changes
    setSelectedLeadIds([]);
    setSelectAll(false);
  }, [leadFilter, dataSourceFilter, dateFrom, dateTo, selectedAgentId]);

  const fetchAgents = async () => {
    setIsLoading(true);
    try {
      const response = await axios.get(`${API}/leads-management/agent-performance`);
      setAgents(response.data.agents || []);
    } catch (error) {
      console.error("Failed to fetch agents:", error);
      toast.error("Failed to load agent data");
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgentLeads = async (agentId) => {
    setIsLoadingLeads(true);
    try {
      // Fetch all leads for this agent
      const response = await axios.get(`${API}/leads?assigned_to=${agentId}&limit=500`);
      // Handle both array and object responses
      const leads = Array.isArray(response.data) ? response.data : (response.data.leads || []);
      setAgentLeads(leads);
      
      // Get agent data for display
      const agentData = agents.find(a => a.agent_id === agentId);
      setSelectedAgentData(agentData);
    } catch (error) {
      console.error("Failed to fetch agent leads:", error);
      toast.error("Failed to load leads");
    } finally {
      setIsLoadingLeads(false);
    }
  };

  const applyFilter = () => {
    let filtered = [...agentLeads];
    const now = new Date();
    const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
    const fifteenDaysAgo = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const fortyFiveDaysAgo = new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000);

    // Apply data source filter first
    if (dataSourceFilter && dataSourceFilter !== "_all_") {
      filtered = filtered.filter(lead => lead.data_source === dataSourceFilter);
    }

    // Apply date range filter (based on allocated_at or created_at)
    if (dateFrom) {
      const fromDate = new Date(dateFrom);
      fromDate.setHours(0, 0, 0, 0);
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.allocated_at || lead.created_at);
        return leadDate >= fromDate;
      });
    }
    if (dateTo) {
      const toDate = new Date(dateTo);
      toDate.setHours(23, 59, 59, 999);
      filtered = filtered.filter(lead => {
        const leadDate = new Date(lead.allocated_at || lead.created_at);
        return leadDate <= toDate;
      });
    }

    switch (leadFilter) {
      case "untouched":
        // Leads not touched in 7+ days or never touched
        filtered = filtered.filter(lead => {
          if (!lead.last_touched_at || lead.last_touched_at === '') return true;
          const touchedDate = new Date(lead.last_touched_at);
          return touchedDate < sevenDaysAgo;
        });
        break;
      case "no_lfb":
        // Leads where LFB is not created
        filtered = filtered.filter(lead => !lead.lfb_created);
        break;
      case "no_lfb_15":
        // Leads with no LFB created, allocated 15+ days ago
        filtered = filtered.filter(lead => {
          if (lead.lfb_created) return false;
          const allocatedDate = new Date(lead.allocated_at || lead.created_at);
          return allocatedDate <= fifteenDaysAgo;
        });
        break;
      case "no_lfb_30":
        // Leads with no LFB created, allocated 30+ days ago
        filtered = filtered.filter(lead => {
          if (lead.lfb_created) return false;
          const allocatedDate = new Date(lead.allocated_at || lead.created_at);
          return allocatedDate <= thirtyDaysAgo;
        });
        break;
      case "no_lfb_45":
        // Leads with no LFB created, allocated 45+ days ago
        filtered = filtered.filter(lead => {
          if (lead.lfb_created) return false;
          const allocatedDate = new Date(lead.allocated_at || lead.created_at);
          return allocatedDate <= fortyFiveDaysAgo;
        });
        break;
      case "has_lfb":
        // Leads where LFB is created
        filtered = filtered.filter(lead => lead.lfb_created === true);
        break;
      default:
        // "all" - no filter
        break;
    }

    setFilteredLeads(filtered);
  };

  // Get counts for LFB filters
  const getLfbFilterCounts = () => {
    const now = new Date();
    const fifteenDaysAgo = new Date(now.getTime() - 15 * 24 * 60 * 60 * 1000);
    const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
    const fortyFiveDaysAgo = new Date(now.getTime() - 45 * 24 * 60 * 60 * 1000);
    
    const noLfbLeads = agentLeads.filter(l => !l.lfb_created);
    
    return {
      no_lfb_15: noLfbLeads.filter(l => new Date(l.allocated_at || l.created_at) <= fifteenDaysAgo).length,
      no_lfb_30: noLfbLeads.filter(l => new Date(l.allocated_at || l.created_at) <= thirtyDaysAgo).length,
      no_lfb_45: noLfbLeads.filter(l => new Date(l.allocated_at || l.created_at) <= fortyFiveDaysAgo).length
    };
  };

  // Get unique data sources from agent leads
  const getDataSources = () => {
    const sources = [...new Set(agentLeads.map(l => l.data_source).filter(Boolean))];
    return sources;
  };

  const handleSelectAll = (checked) => {
    setSelectAll(checked);
    if (checked) {
      setSelectedLeadIds(filteredLeads.map(lead => lead.id));
    } else {
      setSelectedLeadIds([]);
    }
  };

  const handleSelectLead = (leadId, checked) => {
    if (checked) {
      setSelectedLeadIds([...selectedLeadIds, leadId]);
    } else {
      setSelectedLeadIds(selectedLeadIds.filter(id => id !== leadId));
      setSelectAll(false);
    }
  };

  const handleReallocate = async () => {
    if (selectedLeadIds.length === 0) {
      toast.error("No leads selected");
      return;
    }

    setIsReallocating(true);
    try {
      if (targetAgentId === "_unassign_") {
        // Deallocate (unassign)
        const response = await axios.post(`${API}/leads/deallocate`, {
          lead_ids: selectedLeadIds
        });
        toast.success(`${response.data.deallocated_count} leads unassigned and available for reallocation`);
      } else {
        // Reallocate to new agent
        const response = await axios.post(`${API}/leads/mass-allocate`, {
          lead_ids: selectedLeadIds,
          agent_id: targetAgentId
        });
        const targetAgent = agents.find(a => a.agent_id === targetAgentId);
        toast.success(`${response.data.allocated_count} leads assigned to ${targetAgent?.agent_name || 'agent'}`);
      }

      // Refresh data
      setShowReallocateDialog(false);
      setSelectedLeadIds([]);
      setSelectAll(false);
      setTargetAgentId("_unassign_");
      fetchAgents();
      fetchAgentLeads(selectedAgentId);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to reallocate leads");
    } finally {
      setIsReallocating(false);
    }
  };

  const getLeadStatusBadge = (lead) => {
    if (lead.lfb_created) {
      return <Badge className="bg-green-100 text-green-700 text-xs">LFB Created</Badge>;
    }
    if (!lead.last_touched_at || lead.last_touched_at === '') {
      return <Badge className="bg-red-100 text-red-700 text-xs">Never Touched</Badge>;
    }
    const touchedDate = new Date(lead.last_touched_at);
    const daysSinceTouched = Math.floor((new Date() - touchedDate) / (1000 * 60 * 60 * 24));
    if (daysSinceTouched >= 7) {
      return <Badge className="bg-orange-100 text-orange-700 text-xs">Inactive {daysSinceTouched}d</Badge>;
    }
    return <Badge className="bg-blue-100 text-blue-700 text-xs">Active</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return "Never";
    try {
      return new Date(dateStr).toLocaleDateString('en-IN', {
        day: 'numeric',
        month: 'short',
        year: '2-digit'
      });
    } catch {
      return "Invalid";
    }
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Lead Deallocation
          </h1>
          <p className="text-slate-500 mt-1">Remove leads from slow agents & reassign to active ones</p>
        </div>
        <Button 
          variant="outline" 
          onClick={() => { fetchAgents(); if (selectedAgentId !== "_select_") fetchAgentLeads(selectedAgentId); }}
          className="flex items-center gap-2"
          data-testid="refresh-btn"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </Button>
      </div>

      {/* Agent Overview Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {agents.map((agent) => (
          <Card 
            key={agent.agent_id}
            className={`cursor-pointer transition-all hover:shadow-md ${
              selectedAgentId === agent.agent_id 
                ? 'ring-2 ring-blue-500 bg-blue-50/50' 
                : 'hover:bg-slate-50'
            }`}
            onClick={() => setSelectedAgentId(agent.agent_id)}
            data-testid={`agent-card-${agent.agent_id}`}
          >
            <CardContent className="pt-4 pb-3">
              <div className="flex items-start justify-between mb-3">
                <div className="flex items-center gap-2">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <span className="text-blue-700 font-semibold">
                      {agent.agent_name?.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div>
                    <p className="font-medium text-slate-900 text-sm">{agent.agent_name}</p>
                    <p className="text-xs text-slate-500">{agent.total_leads} leads</p>
                  </div>
                </div>
                {selectedAgentId === agent.agent_id && (
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                )}
              </div>
              
              <div className="grid grid-cols-3 gap-2 text-center border-t pt-3">
                <div>
                  <p className={`text-lg font-bold ${agent.untouched_7_days > 0 ? 'text-red-600' : 'text-slate-400'}`}>
                    {agent.untouched_7_days}
                  </p>
                  <p className="text-xs text-slate-500">Untouched</p>
                </div>
                <div>
                  <p className={`text-lg font-bold ${agent.pending_followup_15_days > 0 ? 'text-orange-600' : 'text-slate-400'}`}>
                    {agent.pending_followup_15_days}
                  </p>
                  <p className="text-xs text-slate-500">No LFB</p>
                </div>
                <div>
                  <p className="text-lg font-bold text-green-600">{agent.lfb_created}</p>
                  <p className="text-xs text-slate-500">LFB Done</p>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Selected Agent Lead View */}
      {selectedAgentId !== "_select_" && (
        <Card className="border-slate-200">
          <CardHeader className="pb-3 border-b">
            <div className="flex flex-col gap-4">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <CardTitle className="text-lg font-semibold text-slate-900 flex items-center gap-2">
                  <Eye className="w-5 h-5 text-blue-600" />
                  Leads assigned to {selectedAgentData?.agent_name}
                  <Badge variant="outline" className="ml-2">{filteredLeads.length} of {agentLeads.length}</Badge>
                </CardTitle>
                
                <div className="flex items-center gap-3 flex-wrap">
                  {/* Date Range Filter */}
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-500" />
                    <Input
                      type="date"
                      value={dateFrom}
                      onChange={(e) => setDateFrom(e.target.value)}
                      className="w-[140px] h-9 text-sm"
                      placeholder="From"
                      data-testid="date-from-filter"
                    />
                    <span className="text-slate-400">to</span>
                    <Input
                      type="date"
                      value={dateTo}
                      onChange={(e) => setDateTo(e.target.value)}
                      className="w-[140px] h-9 text-sm"
                      placeholder="To"
                      data-testid="date-to-filter"
                    />
                    {(dateFrom || dateTo) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => { setDateFrom(""); setDateTo(""); }}
                        className="h-9 px-2 text-slate-500 hover:text-red-600"
                      >
                        Clear
                      </Button>
                    )}
                  </div>

                  {/* Data Source Filter */}
                  <Select value={dataSourceFilter} onValueChange={setDataSourceFilter}>
                    <SelectTrigger className="w-[180px]" data-testid="data-source-filter">
                      <SelectValue placeholder="All Data Sources" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="_all_">All Data Sources</SelectItem>
                      {getDataSources().map((source) => (
                        <SelectItem key={source} value={source}>
                          {source}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Filter Tabs - Second Row */}
              <div className="flex items-center justify-between flex-wrap gap-2">
                {/* Primary Filter Tabs */}
                <div className="flex flex-wrap gap-2">
                  <div className="flex rounded-lg border border-slate-200 p-1 bg-slate-50">
                    <button
                      onClick={() => setLeadFilter("all")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "all" 
                          ? "bg-white shadow text-slate-900 font-medium" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="filter-all"
                    >
                      All ({agentLeads.length})
                    </button>
                    <button
                      onClick={() => setLeadFilter("untouched")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "untouched" 
                          ? "bg-white shadow text-red-600 font-medium" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="filter-untouched"
                    >
                      Untouched ({agentLeads.filter(l => !l.last_touched_at || l.last_touched_at === '' || new Date(l.last_touched_at) < new Date(Date.now() - 7*24*60*60*1000)).length})
                    </button>
                    <button
                      onClick={() => setLeadFilter("has_lfb")}
                      className={`px-3 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "has_lfb" 
                          ? "bg-white shadow text-green-600 font-medium" 
                          : "text-slate-600 hover:text-slate-900"
                      }`}
                      data-testid="filter-has-lfb"
                    >
                      LFB Done ({agentLeads.filter(l => l.lfb_created).length})
                    </button>
                  </div>
                  
                  {/* No LFB Duration Filters */}
                  <div className="flex rounded-lg border border-orange-200 p-1 bg-orange-50">
                    <span className="px-2 py-1.5 text-xs font-medium text-orange-700">No LFB:</span>
                    <button
                      onClick={() => setLeadFilter("no_lfb")}
                      className={`px-2 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "no_lfb" 
                          ? "bg-white shadow text-orange-600 font-medium" 
                          : "text-orange-600 hover:text-orange-800"
                      }`}
                      data-testid="filter-no-lfb"
                    >
                      All ({agentLeads.filter(l => !l.lfb_created).length})
                    </button>
                    <button
                      onClick={() => setLeadFilter("no_lfb_15")}
                      className={`px-2 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "no_lfb_15" 
                          ? "bg-white shadow text-orange-600 font-medium" 
                          : "text-orange-600 hover:text-orange-800"
                      }`}
                      data-testid="filter-no-lfb-15"
                    >
                      15d+ ({getLfbFilterCounts().no_lfb_15})
                    </button>
                    <button
                      onClick={() => setLeadFilter("no_lfb_30")}
                      className={`px-2 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "no_lfb_30" 
                          ? "bg-white shadow text-orange-600 font-medium" 
                          : "text-orange-600 hover:text-orange-800"
                      }`}
                      data-testid="filter-no-lfb-30"
                    >
                      30d+ ({getLfbFilterCounts().no_lfb_30})
                    </button>
                    <button
                      onClick={() => setLeadFilter("no_lfb_45")}
                      className={`px-2 py-1.5 text-sm rounded-md transition-colors ${
                        leadFilter === "no_lfb_45" 
                          ? "bg-white shadow text-red-600 font-medium" 
                          : "text-red-500 hover:text-red-700"
                      }`}
                      data-testid="filter-no-lfb-45"
                    >
                      45d+ ({getLfbFilterCounts().no_lfb_45})
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </CardHeader>
          
          <CardContent className="p-0">
            {isLoadingLeads ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
              </div>
            ) : filteredLeads.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 text-slate-400">
                <Filter className="w-10 h-10 mb-2" />
                <p>No leads match this filter</p>
              </div>
            ) : (
              <>
                {/* Action Bar */}
                <div className="flex items-center justify-between px-4 py-3 bg-slate-50 border-b">
                  <div className="flex items-center gap-3">
                    <Checkbox
                      checked={selectAll}
                      onCheckedChange={handleSelectAll}
                      data-testid="select-all-checkbox"
                    />
                    <span className="text-sm text-slate-600">
                      {selectedLeadIds.length > 0 
                        ? `${selectedLeadIds.length} selected` 
                        : `Select all ${filteredLeads.length} leads`}
                    </span>
                  </div>
                  
                  {selectedLeadIds.length > 0 && (
                    <Button
                      onClick={() => setShowReallocateDialog(true)}
                      className="bg-blue-600 hover:bg-blue-700"
                      data-testid="reallocate-btn"
                    >
                      <UserMinus className="w-4 h-4 mr-2" />
                      Deallocate / Reassign ({selectedLeadIds.length})
                    </Button>
                  )}
                </div>

                {/* Leads Table */}
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-slate-50 border-b">
                      <tr>
                        <th className="w-12 px-4 py-3"></th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Lead</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Company</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Data Source</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Allocated On</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Status</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Last Call</th>
                        <th className="text-left text-xs font-semibold text-slate-600 uppercase tracking-wide px-4 py-3">Activity</th>
                        <th className="w-16 px-4 py-3"></th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {filteredLeads.map((lead) => (
                        <tr 
                          key={lead.id} 
                          className={`hover:bg-slate-50 ${selectedLeadIds.includes(lead.id) ? 'bg-blue-50' : ''}`}
                          data-testid={`lead-row-${lead.id}`}
                        >
                          <td className="px-4 py-3">
                            <Checkbox
                              checked={selectedLeadIds.includes(lead.id)}
                              onCheckedChange={(checked) => handleSelectLead(lead.id, checked)}
                            />
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex items-center gap-3">
                              <div className="w-9 h-9 bg-slate-100 rounded-full flex items-center justify-center">
                                <span className="text-slate-600 font-medium text-sm">
                                  {(lead.company_name || lead.name)?.charAt(0).toUpperCase()}
                                </span>
                              </div>
                              <div>
                                <p className="font-medium text-slate-900 text-sm">{lead.company_name || lead.name}</p>
                                <p className="text-xs text-slate-500">{lead.phone}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-3">
                            <p className="text-sm text-slate-700">{lead.contact_person || '-'}</p>
                          </td>
                          <td className="px-4 py-3">
                            {lead.data_source ? (
                              <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-700">
                                {lead.data_source}
                              </span>
                            ) : (
                              <span className="text-xs text-slate-400">-</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDate(lead.allocated_at || lead.created_at)}
                          </td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                              lead.status === 'new' ? 'bg-blue-100 text-blue-700' :
                              lead.status === 'contacted' ? 'bg-yellow-100 text-yellow-700' :
                              lead.status === 'qualified' ? 'bg-purple-100 text-purple-700' :
                              lead.status === 'converted_to_lfb' ? 'bg-green-100 text-green-700' :
                              lead.status === 'lost' ? 'bg-red-100 text-red-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>
                              {lead.status?.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-slate-600">
                            {formatDate(lead.last_call_date)}
                          </td>
                          <td className="px-4 py-3">
                            {getLeadStatusBadge(lead)}
                          </td>
                          <td className="px-4 py-3">
                            <Link 
                              to={`/leads/${lead.id}`}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              <ChevronRight className="w-5 h-5" />
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Empty state when no agent selected */}
      {selectedAgentId === "_select_" && (
        <Card className="border-dashed border-2 border-slate-200 bg-slate-50/50">
          <CardContent className="flex flex-col items-center justify-center py-16">
            <Users className="w-12 h-12 text-slate-300 mb-4" />
            <p className="text-lg font-medium text-slate-600 mb-1">Select an Agent</p>
            <p className="text-sm text-slate-400">Click on an agent card above to view their leads</p>
          </CardContent>
        </Card>
      )}

      {/* Reallocate/Deallocate Dialog */}
      <Dialog open={showReallocateDialog} onOpenChange={setShowReallocateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserMinus className="w-5 h-5 text-blue-600" />
              Deallocate / Reassign Leads
            </DialogTitle>
            <DialogDescription>
              {selectedLeadIds.length} leads selected from {selectedAgentData?.agent_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-slate-50 p-4 rounded-lg">
              <p className="text-sm text-slate-600 mb-2">Current filter: <span className="font-medium capitalize">{leadFilter.replace('_', ' ')}</span></p>
              <p className="text-2xl font-bold text-slate-900">{selectedLeadIds.length} leads</p>
            </div>
            
            <div className="space-y-2">
              <Label>Action</Label>
              <Select value={targetAgentId} onValueChange={setTargetAgentId}>
                <SelectTrigger data-testid="target-agent-select">
                  <SelectValue placeholder="Select action" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_unassign_">
                    <span className="flex items-center gap-2">
                      <XCircle className="w-4 h-4 text-slate-400" />
                      Unassign (make available for reallocation)
                    </span>
                  </SelectItem>
                  {agents
                    .filter(a => a.agent_id !== selectedAgentId)
                    .map((agent) => (
                      <SelectItem key={agent.agent_id} value={agent.agent_id}>
                        <span className="flex items-center gap-2">
                          <UserPlus className="w-4 h-4 text-blue-500" />
                          Assign to {agent.agent_name}
                        </span>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
            </div>
            
            {targetAgentId !== "_unassign_" && (
              <div className="bg-blue-50 p-3 rounded-lg text-sm text-blue-700">
                <p className="font-medium mb-1">Reassigning to: {agents.find(a => a.agent_id === targetAgentId)?.agent_name}</p>
                <p className="text-blue-600">Current load: {agents.find(a => a.agent_id === targetAgentId)?.total_leads} leads</p>
              </div>
            )}
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowReallocateDialog(false)}>
              Cancel
            </Button>
            <Button 
              onClick={handleReallocate}
              disabled={isReallocating}
              className="bg-blue-600 hover:bg-blue-700 text-white"
              data-testid="confirm-reallocate-btn"
            >
              {isReallocating ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : targetAgentId === "_unassign_" ? (
                <>
                  <UserMinus className="w-4 h-4 mr-2" />
                  Unassign Leads
                </>
              ) : (
                <>
                  <UserPlus className="w-4 h-4 mr-2" />
                  Reassign Leads
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LeadManagement;
